/**
 * KKG Block „Galerie" (blocks/gallery) – Lightbox
 *
 * Das Raster kommt komplett aus PHP (render.php). Dieses Modul öffnet pro
 * Block ein natives <dialog> als Fullscreen-Lightbox:
 *  - showModal(): Top-Layer, Fokusfalle, Hintergrund inert, Escape schließt nativ
 *  - EIN <img> in der Lightbox; beim Öffnen/Blättern werden src/srcset/sizes/alt
 *    aus den data-Attributen des angeklickten Buttons gesetzt (Größe 2048x2048).
 *    Nachbarbilder werden einmalig vorgeladen (Browser-Cache), kein DOM-Aufbau.
 *  - Navigation: Pfeiltasten, Buttons, Swipe (GSAP Observer), Klick auf die
 *    dunkle Fläche schließt. Wrap-around am Ende.
 *  - Fokus: beim Öffnen auf „Schließen", beim Schließen zurück auf den Auslöser.
 *  - Screenreader: Live-Region „Bild 3 von 12: …" (aria-live=polite).
 *  - Body-Scroll: html.kkg-lightbox-open + ScrollSmoother.paused(true).
 *  - prefers-reduced-motion: keine Blenden (Dauer 0).
 *  - Aufräumen: Keydown-Listener und Observer existieren nur, solange die
 *    Lightbox offen ist; Tweens werden gekillt.
 *
 * Markup: blocks/gallery/render.php
 * Styles: src/sass/theme/_kkg_components.scss (Abschnitt „9d. Galerie")
 */
import { gsap } from "gsap";
import { Observer } from "gsap/Observer";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(Observer, ScrollSmoother);

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

// Blenddauer (s); 0 bei reduzierter Bewegung
const fadeDuration = () => (reduceMotion.matches ? 0 : 0.25);

// Mindestweg (px) für einen Swipe
const SWIPE_TOLERANCE = 40;

/**
 * Initialisiert alle Galerie-Blöcke der Seite.
 */
export function initGalleries() {
    document.querySelectorAll("[data-kkg-gallery]").forEach(setupGallery);
}

function setupGallery(block) {
    const dialog = block.querySelector("[data-kkg-lightbox]");
    const triggers = Array.from(block.querySelectorAll("[data-kkg-gallery-open]"));

    // Ohne <dialog>-Unterstützung (sehr alte Browser) bleiben die Bilder statisch.
    if (!dialog || !triggers.length || typeof dialog.showModal !== "function") {
        return null;
    }

    const img = dialog.querySelector("[data-kkg-lightbox-img]");
    const figure = dialog.querySelector(".kkg-lightbox__figure");
    const caption = dialog.querySelector("[data-kkg-lightbox-caption]");
    const counter = dialog.querySelector("[data-kkg-lightbox-counter]");
    const status = dialog.querySelector("[data-kkg-lightbox-status]");
    const closeBtn = dialog.querySelector("[data-kkg-lightbox-close]");
    const prevBtn = dialog.querySelector("[data-kkg-lightbox-prev]");
    const nextBtn = dialog.querySelector("[data-kkg-lightbox-next]");

    // Bilddaten einmal aus den Buttons lesen (keine weiteren DOM-Zugriffe beim Blättern)
    const items = triggers.map((trigger) => ({
        trigger,
        src: trigger.dataset.src || "",
        srcset: trigger.dataset.srcset || "",
        width: parseInt(trigger.dataset.width, 10) || 0,
        height: parseInt(trigger.dataset.height, 10) || 0,
        alt: trigger.dataset.alt || "",
        caption: trigger.dataset.caption || "",
    }));
    const total = items.length;
    const preloaded = new Set();

    let index = 0;
    let opener = null; // auslösender Button (Fokus-Rückgabe)
    let observer = null; // GSAP Observer für Swipe, nur bei offener Lightbox
    let fadeTween = null;
    let renderToken = 0; // schützt vor Überholern bei schnellem Blättern

    // Nachbarbild einmalig vorladen: gleiche srcset/sizes, damit der Browser
    // denselben Kandidaten wählt und das Bild später aus dem Cache kommt.
    const preload = (i) => {
        const n = (i + total) % total;
        if (preloaded.has(n)) {
            return;
        }
        preloaded.add(n);
        const probe = new Image();
        probe.sizes = "100vw";
        if (items[n].srcset) {
            probe.srcset = items[n].srcset;
        }
        probe.src = items[n].src;
    };

    // Bild + Texte in die Lightbox setzen (ein <img>, keine neuen Knoten)
    const apply = (item) => {
        img.sizes = "100vw";
        img.srcset = item.srcset;
        img.src = item.src;
        if (item.width && item.height) {
            img.width = item.width;
            img.height = item.height;
        }
        img.alt = item.alt || item.caption;

        // Beschreibung nur ausgeben, wenn vorhanden – sonst kein leerer Container
        if (item.caption) {
            caption.textContent = item.caption;
            caption.hidden = false;
        } else {
            caption.textContent = "";
            caption.hidden = true;
        }

        const position = `${index + 1} / ${total}`;
        counter.textContent = position;
        const name = item.alt || item.caption;
        status.textContent = `Bild ${index + 1} von ${total}` + (name ? `: ${name}` : "");
    };

    // Zu Bild i wechseln (mit Wrap-around), weich überblenden
    const show = (i, immediate) => {
        index = ((i % total) + total) % total;
        const item = items[index];
        const token = ++renderToken;
        const duration = immediate ? 0 : fadeDuration();

        if (fadeTween) {
            fadeTween.kill();
        }

        const swapAndFadeIn = () => {
            if (token !== renderToken) {
                return;
            }
            apply(item);
            preload(index + 1);
            preload(index - 1);
            const fadeIn = () => {
                if (token !== renderToken) {
                    return;
                }
                fadeTween = gsap.to(figure, { opacity: 1, duration, ease: "power2.out", overwrite: true });
            };
            // Erst einblenden, wenn das Bild dekodiert ist (kein Aufblitzen des alten Bildes)
            if (typeof img.decode === "function") {
                img.decode().then(fadeIn, fadeIn);
            } else {
                fadeIn();
            }
        };

        if (duration === 0) {
            gsap.set(figure, { opacity: 0 });
            swapAndFadeIn();
        } else {
            fadeTween = gsap.to(figure, { opacity: 0, duration: duration * 0.6, ease: "power2.in", overwrite: true, onComplete: swapAndFadeIn });
        }
    };

    const next = () => show(index + 1);
    const prev = () => show(index - 1);

    // Tastatur (Escape übernimmt das <dialog> selbst)
    const onKeydown = (event) => {
        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                event.preventDefault();
                next();
                break;
            case "ArrowLeft":
            case "ArrowUp":
                event.preventDefault();
                prev();
                break;
            case "Home":
                event.preventDefault();
                show(0);
                break;
            case "End":
                event.preventDefault();
                show(total - 1);
                break;
            default:
        }
    };

    // Klick auf die dunkle Fläche (nicht auf Bild, Beschreibung oder Buttons) schließt
    const onDialogClick = (event) => {
        if (!event.target.closest("[data-kkg-lightbox-content], .kkg-lightbox__btn")) {
            requestClose();
        }
    };

    const open = (i, trigger) => {
        if (dialog.open) {
            return;
        }
        opener = trigger;

        // Body-Scroll sperren: Klasse (overflow hidden) + Smoother anhalten
        document.documentElement.classList.add("kkg-lightbox-open");
        const smoother = ScrollSmoother.get();
        if (smoother) {
            smoother.paused(true);
        }

        show(i, true);
        dialog.showModal();
        closeBtn.focus();

        gsap.fromTo(dialog, { opacity: 0 }, { opacity: 1, duration: fadeDuration(), ease: "power2.out", overwrite: true });

        dialog.addEventListener("keydown", onKeydown);
        dialog.addEventListener("click", onDialogClick);

        // Swipe (Touch/Pointer) – lebt nur, solange die Lightbox offen ist.
        // preventDefault verhindert auf Touch das Scrollen der Seite hinter dem Dialog.
        // onLeft/onRight feuern bei jeder Bewegung über der Toleranz, deshalb
        // pro Geste (press … release) nur ein Bildwechsel.
        let swiped = false;
        observer = Observer.create({
            target: dialog,
            type: "touch,pointer",
            tolerance: SWIPE_TOLERANCE,
            lockAxis: true,
            preventDefault: true,
            allowClicks: true,
            onPress: () => { swiped = false; },
            onRelease: () => { swiped = false; },
            onLeft: () => { if (!swiped) { swiped = true; next(); } },
            onRight: () => { if (!swiped) { swiped = true; prev(); } },
        });
    };

    // Weich ausblenden, dann nativ schließen (löst „close" aus)
    const requestClose = () => {
        if (!dialog.open) {
            return;
        }
        gsap.to(dialog, {
            opacity: 0,
            duration: fadeDuration(),
            ease: "power2.in",
            overwrite: true,
            onComplete: () => dialog.close(),
        });
    };

    // Aufräumen nach jedem Schließen (Button, Escape, Klick auf die Fläche)
    const onClosed = () => {
        dialog.removeEventListener("keydown", onKeydown);
        dialog.removeEventListener("click", onDialogClick);
        if (observer) {
            observer.kill();
            observer = null;
        }
        if (fadeTween) {
            fadeTween.kill();
            fadeTween = null;
        }
        gsap.killTweensOf(dialog);
        gsap.set(dialog, { clearProps: "opacity" });

        document.documentElement.classList.remove("kkg-lightbox-open");
        const smoother = ScrollSmoother.get();
        if (smoother) {
            smoother.paused(false);
        }

        // Fokus zurück zum auslösenden Bild
        if (opener && typeof opener.focus === "function") {
            opener.focus({ preventScroll: true });
        }
        opener = null;
    };

    triggers.forEach((trigger, i) => {
        trigger.addEventListener("click", () => open(i, trigger));
    });
    closeBtn.addEventListener("click", requestClose);
    prevBtn.addEventListener("click", prev);
    nextBtn.addEventListener("click", next);
    dialog.addEventListener("close", onClosed);

    // Debug-Zugriff (Konsole: kkgGalleries[0].open(2))
    window.kkgGalleries = window.kkgGalleries || [];
    window.kkgGalleries.push({ block, dialog, open: (i) => open(i, items[i] ? items[i].trigger : null), close: requestClose, next, prev, get index() { return index; } });

    return dialog;
}
